import { MagnifyingGlassIcon } from '@phosphor-icons/react'
import { Dialog as D } from 'radix-ui'
import { Fragment, useEffect, useId, useMemo, useRef, useState, type KeyboardEvent, type ReactNode } from 'react'
import { highlight, rankCommands, type CommandLike } from '../logic/commands.ts'
import { Keys } from './Kbd.tsx'

export interface PaletteCommand extends CommandLike {
  id: string
  title: string
  group: string
  keywords?: readonly string[]
  icon?: ReactNode
  /** Right-aligned context (a path, a kind). */
  hint?: ReactNode
  /** Shortcut, shown as keycaps. */
  keys?: string
  run: () => void
}

export interface CommandPaletteProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  commands: readonly PaletteCommand[]
  /** Commands derived from the query itself (pasted ids, skill keys). Shown first. */
  resolve?: (query: string) => readonly PaletteCommand[]
  /** Commands shown after the ranked results (e.g. "search the library for …"). */
  fallback?: (query: string) => readonly PaletteCommand[]
  placeholder?: string
  /** Ids of recently run commands, most recent first. */
  recent?: readonly string[]
  onRun?: (cmd: PaletteCommand) => void
  groupOrder?: readonly string[]
}

interface Row {
  cmd: PaletteCommand
  ranges: [number, number][]
  group: string
}

/** Builds the visible rows for a query: resolved ids, then ranked commands grouped by best score. */
export function paletteRows(
  query: string,
  commands: readonly PaletteCommand[],
  resolve?: (q: string) => readonly PaletteCommand[],
  recent: readonly string[] = [],
  groupOrder: readonly string[] = [],
  fallback?: (q: string) => readonly PaletteCommand[],
): Row[] {
  const resolved = query.trim() ? (resolve?.(query) ?? []) : []
  const seen = new Set(resolved.map((c) => c.id))
  const rows: Row[] = resolved.map((cmd) => ({ cmd, ranges: [], group: cmd.group }))
  if (!query.trim()) {
    const byId = new Map(commands.map((c) => [c.id, c]))
    for (const id of recent.slice(0, 4)) {
      const c = byId.get(id)
      if (c && !seen.has(id)) {
        rows.push({ cmd: c, ranges: [], group: 'Recent' })
        seen.add(id)
      }
    }
    const order = (g: string) => {
      const i = groupOrder.indexOf(g)
      return i < 0 ? groupOrder.length : i
    }
    const rest = commands.filter((c) => !seen.has(c.id))
    const groups = [...new Set(rest.map((c) => c.group))].sort((a, b) => order(a) - order(b))
    for (const g of groups) for (const c of rest) if (c.group === g) rows.push({ cmd: c, ranges: [], group: g })
    return rows
  }
  const ranked = rankCommands(query, commands.filter((c) => !seen.has(c.id)))
  const best = new Map<string, number>()
  for (const r of ranked) best.set(r.command.group, Math.max(best.get(r.command.group) ?? -Infinity, r.score))
  const groups = [...best.keys()].sort((a, b) => best.get(b)! - best.get(a)!)
  for (const g of groups) for (const r of ranked) if (r.command.group === g) rows.push({ cmd: r.command, ranges: r.ranges, group: g })
  for (const cmd of fallback?.(query) ?? []) if (!seen.has(cmd.id)) rows.push({ cmd, ranges: [], group: cmd.group })
  return rows
}

/** ⌘K: open any page, run any action, resolve any id. */
export function CommandPalette({ open, onOpenChange, commands, resolve, fallback, placeholder = 'Search pages and actions, or paste an id…', recent, onRun, groupOrder }: CommandPaletteProps) {
  const [query, setQuery] = useState('')
  const [active, setActive] = useState(0)
  const listRef = useRef<HTMLDivElement>(null)
  const uid = useId().replace(/:/g, '')
  const rows = useMemo(() => paletteRows(query, commands, resolve, recent, groupOrder, fallback), [query, commands, resolve, recent, groupOrder, fallback])

  useEffect(() => {
    if (!open) setQuery('')
  }, [open])
  useEffect(() => setActive(0), [query])
  useEffect(() => {
    const el = listRef.current?.querySelector<HTMLElement>(`[data-index="${active}"]`)
    el?.scrollIntoView({ block: 'nearest' })
  }, [active])

  const run = (row: Row | undefined) => {
    if (!row) return
    onOpenChange(false)
    onRun?.(row.cmd)
    // let the dialog close (and focus return) before navigating
    window.setTimeout(() => row.cmd.run(), 0)
  }

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown' || (e.ctrlKey && e.key === 'n')) {
      e.preventDefault()
      setActive((i) => (rows.length ? (i + 1) % rows.length : 0))
    } else if (e.key === 'ArrowUp' || (e.ctrlKey && e.key === 'p')) {
      e.preventDefault()
      setActive((i) => (rows.length ? (i - 1 + rows.length) % rows.length : 0))
    } else if (e.key === 'Enter') {
      e.preventDefault()
      run(rows[active])
    } else if (e.key === 'Home' && e.ctrlKey) {
      setActive(0)
    }
  }

  let lastGroup = ''
  return (
    <D.Root open={open} onOpenChange={onOpenChange}>
      <D.Portal>
        <D.Overlay className="rt-scrim" data-palette="" />
        <D.Content className="rt-palette" aria-describedby={undefined}>
          <D.Title className="rt-visually-hidden">Command palette</D.Title>
          <div className="rt-palette__input">
            <MagnifyingGlassIcon aria-hidden />
            <input
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={onKeyDown}
              placeholder={placeholder}
              role="combobox"
              aria-expanded="true"
              aria-controls={`${uid}-list`}
              aria-activedescendant={rows[active] ? `${uid}-o-${active}` : undefined}
              aria-autocomplete="list"
              spellCheck={false}
              autoComplete="off"
            />
            <kbd className="rt-kbd">Esc</kbd>
          </div>
          <div ref={listRef} id={`${uid}-list`} className="rt-palette__list" role="listbox" aria-label="Results">
            {rows.length === 0 ? (
              <div className="rt-palette__empty">
                No pages or actions match “{query}”. Paste an id like <span className="rt-mono">usr_…</span> or <span className="rt-mono">art_…</span> to open it.
              </div>
            ) : (
              rows.map((row, i) => {
                const header = row.group !== lastGroup ? row.group : null
                lastGroup = row.group
                return (
                  <Fragment key={`${row.group}:${row.cmd.id}`}>
                    {header ? (
                      <div className="rt-palette__group rt-label" role="presentation">
                        {header}
                      </div>
                    ) : null}
                    <div
                      id={`${uid}-o-${i}`}
                      data-index={i}
                      role="option"
                      aria-selected={i === active}
                      className="rt-palette__item"
                      onMouseMove={() => i !== active && setActive(i)}
                      onClick={() => run(row)}
                    >
                      {row.cmd.icon}
                      <span className="rt-palette__title">
                        {highlight(row.cmd.title, row.ranges).map((p, j) => (p.hit ? <mark key={j}>{p.text}</mark> : <Fragment key={j}>{p.text}</Fragment>))}
                      </span>
                      {row.cmd.keys ? <Keys keys={row.cmd.keys} /> : row.cmd.hint ? <span className="rt-palette__hint rt-mono">{row.cmd.hint}</span> : null}
                    </div>
                  </Fragment>
                )
              })
            )}
          </div>
          <div className="rt-palette__foot" aria-hidden>
            <span>
              <kbd className="rt-kbd">↑</kbd>
              <kbd className="rt-kbd">↓</kbd> move
            </span>
            <span>
              <kbd className="rt-kbd">↵</kbd> open
            </span>
            <span>
              <span className="rt-mono">usr_ art_ msn_</span> paste any id
            </span>
          </div>
        </D.Content>
      </D.Portal>
    </D.Root>
  )
}
